import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface Product {
  id: string;
  owner_id: string;
  name: string;
  brand: string;
  reference: string;
  category: string;
  lot_number: string;
  expiration_date: string | null;
  opening_date: string | null;
  stock: number;
  min_stock_alert: number;
  image_url: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

export const PRODUCT_CATEGORIES = [
  { value: 'needles', label: 'Aiguilles' },
  { value: 'inks', label: 'Encres' },
  { value: 'consumables', label: 'Consommables' },
  { value: 'equipment', label: 'Équipement' },
  { value: 'protection', label: 'Protection' },
  { value: 'other', label: 'Autres' },
];

export interface NeedleBrandCatalog {
  brand: string;
  references: string[];
}

export const NEEDLE_CATALOG: NeedleBrandCatalog[] = [
  {
    brand: 'KWADRON',
    references: [
      '25/3RLLT',
      '35/3RLLT',
      '35/3RLLT-T',
      '35/5RLLT',
      '35/7RS',
      '35/9M1',
      '35/13RM',
      '35/15SEM',
    ],
  },
];

export function useProducts() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['products', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('owner_id', user!.id)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as Product[];
    },
    enabled: !!user,
  });
}

export function useLowStockProducts() {
  const { data: products = [] } = useProducts();
  return products.filter(p => p.stock <= p.min_stock_alert);
}

export function useSaveProduct() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (product: Partial<Product> & { name: string }) => {
      const row = {
        name: product.name,
        brand: product.brand || '',
        reference: product.reference || '',
        category: product.category || 'needles',
        lot_number: product.lot_number || '',
        expiration_date: product.expiration_date || null,
        opening_date: product.opening_date || null,
        stock: product.stock ?? 0,
        min_stock_alert: product.min_stock_alert ?? 5,
        image_url: product.image_url || null,
        notes: product.notes || '',
      };
      if (product.id) {
        const { error } = await supabase.from('products').update(row).eq('id', product.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('products').insert({ ...row, owner_id: user!.id });
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('products').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  });
}
